import { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import toast from "react-hot-toast";
import SoftBox from "components/SoftBox";
import SoftButton from "components/SoftButton";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import { RequestForPaymentService } from "services/request-for-payment";
import { formatVndIntegerInput, parseVndIntegerInput } from "utils";
import { getPrintTemplate } from "./components/printTemplate";

const splitDate = (text = "") => {
  const [day = "", month = "", year = ""] = String(text).split("/");
  return {
    day: day.replace(/\D/g, "").slice(0, 2),
    month: month.replace(/\D/g, "").slice(0, 2),
    year: year.replace(/\D/g, "").slice(0, 4),
  };
};

const composeDate = (parts) => {
  if (!parts.day || !parts.month || !parts.year) return "";
  return `${String(parts.day).padStart(2, "0")}/${String(parts.month).padStart(2, "0")}/${parts.year}`;
};

const validateDate = (parts, label) => {
  const date = composeDate(parts);
  if (!date) throw new Error(`${label} chưa hợp lệ`);
  const [d, m, y] = date.split("/").map(Number);
  const maxDay = new Date(y, m, 0).getDate();
  if (m < 1 || m > 12 || y < 1900 || y > 9999 || d < 1 || d > maxDay) {
    throw new Error(`${label} không hợp lệ`);
  }
  return date;
};

const amountToVietnameseWords = (value) => {
  const number = parseVndIntegerInput(value);
  if (!number) return "";

  const chuSo = ["không", "một", "hai", "ba", "bốn", "năm", "sáu", "bảy", "tám", "chín"];
  const tien = ["", "nghìn", "triệu", "tỷ", "nghìn tỷ", "triệu tỷ"];
  const strNumber = String(Math.round(number));
  const chunks = [];

  for (let i = strNumber.length; i > 0; i -= 3) {
    chunks.push(strNumber.substring(Math.max(i - 3, 0), i));
  }

  const docHangChuc = (so, daydu) => {
    let chuoi = "";
    const chuc = Math.floor(so / 10);
    const donvi = so % 10;

    if (chuc > 1) {
      chuoi = ` ${chuSo[chuc]} mươi`;
      if (donvi === 1) chuoi += " mốt";
    } else if (chuc === 1) {
      chuoi = " mười";
      if (donvi === 1) chuoi += " một";
    } else if (daydu && donvi > 0) {
      chuoi = " linh";
    }

    if (donvi === 5 && chuc >= 1) chuoi += " lăm";
    else if (donvi > 1 || (donvi === 1 && chuc === 0)) chuoi += ` ${chuSo[donvi]}`;
    return chuoi;
  };

  const docKhoi = (so, daydu) => {
    const tram = Math.floor(so / 100);
    const soDu = so % 100;
    if (daydu || tram > 0) {
      return ` ${chuSo[tram]} trăm${docHangChuc(soDu, true)}`;
    }
    return docHangChuc(soDu, false);
  };

  let ketQua = "";
  for (let i = chunks.length - 1; i >= 0; i -= 1) {
    const soNhom = parseInt(chunks[i], 10);
    if (soNhom > 0) {
      ketQua += `${docKhoi(soNhom, i !== chunks.length - 1)} ${tien[i]}`;
    }
  }

  const normalized = ketQua.trim();
  if (!normalized) return "";
  return `${normalized.charAt(0).toUpperCase() + normalized.slice(1)} đồng`;
};

export default function RequestForPaymentDetail() {
  const { id } = useParams();
  const location = useLocation();
  const containView = location.pathname?.includes("/view");
  const navigation = useNavigate();

  const today = new Date();
  const [loadingSubmit, setLoadingSubmit] = useState(false);
  const [documentDateParts, setDocumentDateParts] = useState({
    day: String(today.getDate()).padStart(2, "0"),
    month: String(today.getMonth() + 1).padStart(2, "0"),
    year: String(today.getFullYear()),
  });
  const [form, setForm] = useState({
    unit: "",
    address: "",
    documentNumber: "",
    recipient: "",
    requesterName: "",
    requesterDepartmentAddress: "",
    paymentContent: "",
    amountInNumber: "",
    amountInWords: "",
    attachments: "",
    originalDocuments: "",
  });

  useEffect(() => {
    if (!id) return;
    (async () => {
      try {
        const response = await RequestForPaymentService.getById(id);
        const data = response.data ? response.data : response;
        setForm({
          unit: data.unit || "",
          address: data.address || "",
          documentNumber: data.documentNumber || "",
          recipient: data.recipient || "",
          requesterName: data.requesterName || "",
          requesterDepartmentAddress: data.requesterDepartmentAddress || "",
          paymentContent: data.paymentContent || "",
          amountInNumber: formatVndIntegerInput(data.amountInNumber || ""),
          amountInWords: amountToVietnameseWords(data.amountInNumber || "") || data.amountInWords || "",
          attachments: data.attachments || "",
          originalDocuments: data.originalDocuments || "",
        });
        setDocumentDateParts(splitDate(data.documentDate || ""));
      } catch (error) {
        toast.error("Không thể tải dữ liệu giấy đề nghị thanh toán");
      }
    })();
  }, [id]);

  const setField = (key, value) => {
    if (key === "amountInNumber") {
      const formattedValue = formatVndIntegerInput(value);
      setForm((prev) => ({
        ...prev,
        amountInNumber: formattedValue,
        amountInWords: amountToVietnameseWords(formattedValue),
      }));
      return;
    }
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const setDateField = (field, value) => {
    const max = field === "year" ? 4 : 2;
    const raw = value.replace(/\D/g, "");
    setDocumentDateParts((prev) => ({ ...prev, [field]: field === "year" ? raw.slice(0, max) : raw.slice(-max) }));
  };

  const padDateField = (field) => {
    if (field === "year") return;
    setDocumentDateParts((prev) => {
      if (!prev[field]) return prev;
      return { ...prev, [field]: String(prev[field]).padStart(2, "0") };
    });
  };

  const buildPayload = () => {
    const amountInNumberValue = parseVndIntegerInput(form.amountInNumber || "");
    const payload = {
      ...form,
      documentDate: validateDate(documentDateParts, "Ngày chứng từ"),
      unit: String(form.unit || "").trim(),
      address: String(form.address || "").trim(),
      documentNumber: String(form.documentNumber || "").trim(),
      recipient: String(form.recipient || "").trim(),
      requesterName: String(form.requesterName || "").trim(),
      requesterDepartmentAddress: String(form.requesterDepartmentAddress || "").trim(),
      paymentContent: String(form.paymentContent || "").trim(),
      amountInNumber: Number(amountInNumberValue),
      amountInWords: String(form.amountInWords || "").trim(),
      attachments: String(form.attachments || "").trim(),
      originalDocuments: String(form.originalDocuments || "").trim(),
    };

    const required = [
      ["unit", "Đơn vị"],
      ["address", "Địa chỉ"],
      ["documentNumber", "Số chứng từ"],
      ["recipient", "Kính gửi"],
      ["requesterName", "Họ tên người đề nghị thanh toán"],
      ["requesterDepartmentAddress", "Bộ phận (hoặc địa chỉ)"],
      ["paymentContent", "Nội dung thanh toán"],
      ["amountInNumber", "Số tiền"],
      ["amountInWords", "Số tiền (viết bằng chữ)"],
    ];

    for (const [key, label] of required) {
      if (!payload[key]) throw new Error(`${label} không được để trống`);
    }

    if (amountInNumberValue <= 0) throw new Error("Số tiền phải lớn hơn 0");

    return payload;
  };

  const submit = async () => {
    try {
      setLoadingSubmit(true);
      const payload = buildPayload();
      if (!id) {
        await RequestForPaymentService.create(payload);
        toast.success("Tạo giấy đề nghị thanh toán thành công");
      } else {
        await RequestForPaymentService.update(id, payload);
        toast.success("Cập nhật giấy đề nghị thanh toán thành công");
      }
      navigation("/templates/request-for-payment");
    } catch (error) {
      toast.error(error?.response?.data?.message || error.message || "Không thể lưu giấy đề nghị thanh toán");
    } finally {
      setLoadingSubmit(false);
    }
  };

  const handlePrint = () => {
    const htmlContent = getPrintTemplate({
      data: {
        ...form,
        documentDate: composeDate(documentDateParts),
        amountInNumber: parseVndIntegerInput(form.amountInNumber || ""),
      },
    });
    const printWindow = window.open("", "_blank");
    printWindow.document.open();
    printWindow.document.write(htmlContent);
    printWindow.document.close();
  };

  const inputStyle = {
    border: "none",
    borderBottom: "1px dotted #000",
    outline: "none",
    fontSize: 16,
    fontFamily: "inherit",
    background: "transparent",
    minWidth: 20,
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <SoftBox py={3}>
        <Grid container justifyContent="center">
          <Grid item xs={12}>
            <Card>
              <SoftBox
                p={2}
                style={{
                  fontFamily: '"Times New Roman", Times, serif',
                  fontSize: 16,
                  color: "#000",
                  backgroundColor: "#fff",
                  padding: 20,
                }}
              >
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 12 }}>
                  <div>
                    <div>
                      <b>Đơn vị:</b>{" "}
                      <input
                        disabled={containView}
                        style={{ ...inputStyle, width: 220 }}
                        value={form.unit}
                        onChange={(event) => setField("unit", event.target.value)}
                      />
                    </div>
                    <div>
                      <b>Địa chỉ:</b>{" "}
                      <input
                        disabled={containView}
                        style={{ ...inputStyle, width: 220 }}
                        value={form.address}
                        onChange={(event) => setField("address", event.target.value)}
                      />
                    </div>
                  </div>
                  <div style={{ textAlign: "center" }}>
                    <div>
                      <b>Mẫu số 05 - TT</b>
                    </div>
                    <div style={{ fontStyle: "italic", fontSize: 13 }}>
                      (Kèm theo Thông tư số 99/2025/TT-BTC
                      <br />
                      ngày 27 tháng 10 năm 2025 của Bộ trưởng Bộ Tài chính)
                    </div>
                  </div>
                </div>

                <h2 style={{ textAlign: "center", margin: "8px 0 4px", textTransform: "uppercase", fontWeight: 700 }}>
                  GIẤY ĐỀ NGHỊ THANH TOÁN
                </h2>

                <div style={{ textAlign: "center", marginBottom: 8 }}>
                  Ngày
                  <input
                    disabled={containView}
                    style={{ ...inputStyle, width: 34, textAlign: "center", margin: "0 4px" }}
                    value={documentDateParts.day}
                    onChange={(event) => setDateField("day", event.target.value)}
                    onBlur={() => padDateField("day")}
                  />
                  tháng
                  <input
                    disabled={containView}
                    style={{ ...inputStyle, width: 34, textAlign: "center", margin: "0 4px" }}
                    value={documentDateParts.month}
                    onChange={(event) => setDateField("month", event.target.value)}
                    onBlur={() => padDateField("month")}
                  />
                  năm
                  <input
                    disabled={containView}
                    style={{ ...inputStyle, width: 48, textAlign: "center", margin: "0 4px" }}
                    value={documentDateParts.year}
                    onChange={(event) => setDateField("year", event.target.value)}
                  />
                </div>

                <div style={{ textAlign: "right", marginBottom: 10 }}>
                  Số:{" "}
                  <input
                    disabled={containView}
                    style={{ ...inputStyle, width: 160 }}
                    value={form.documentNumber}
                    onChange={(event) => setField("documentNumber", event.target.value)}
                  />
                </div>

                <div style={{ marginBottom: 8 }}>
                  <b>Kính gửi:</b>{" "}
                  <input
                    disabled={containView}
                    style={{ ...inputStyle, width: "74%" }}
                    value={form.recipient}
                    onChange={(event) => setField("recipient", event.target.value)}
                  />
                </div>
                <div style={{ marginBottom: 8 }}>
                  Họ và tên người đề nghị thanh toán:{" "}
                  <input
                    disabled={containView}
                    style={{ ...inputStyle, width: "64%" }}
                    value={form.requesterName}
                    onChange={(event) => setField("requesterName", event.target.value)}
                  />
                </div>
                <div style={{ marginBottom: 8 }}>
                  Bộ phận (Hoặc địa chỉ):{" "}
                  <input
                    disabled={containView}
                    style={{ ...inputStyle, width: "66%" }}
                    value={form.requesterDepartmentAddress}
                    onChange={(event) => setField("requesterDepartmentAddress", event.target.value)}
                  />
                </div>
                <div style={{ marginBottom: 8 }}>
                  Nội dung thanh toán:{" "}
                  <input
                    disabled={containView}
                    style={{ ...inputStyle, width: "72%" }}
                    value={form.paymentContent}
                    onChange={(event) => setField("paymentContent", event.target.value)}
                  />
                </div>
                <div style={{ marginBottom: 8 }}>
                  Số tiền:{" "}
                  <input
                    disabled={containView}
                    style={{ ...inputStyle, width: 180, textAlign: "right" }}
                    value={form.amountInNumber}
                    onChange={(event) => setField("amountInNumber", event.target.value)}
                  />{" "}
                  (Viết bằng chữ):{" "}
                  <input
                    disabled={containView}
                    style={{ ...inputStyle, width: 360 }}
                    value={form.amountInWords}
                    readOnly
                  />
                </div>
                <div style={{ marginBottom: 8, fontStyle: "italic" }}>
                  (Kèm theo{" "}
                  <input
                    disabled={containView}
                    style={{ ...inputStyle, width: 80 }}
                    value={form.attachments}
                    onChange={(event) => setField("attachments", event.target.value)}
                  />{" "}
                  chứng từ gốc).
                </div>

                <SoftBox mt={3} display="flex" justifyContent="space-between">
                  <SoftButton variant="outlined" color="secondary" onClick={() => navigation("/templates/request-for-payment")}>TRỞ VỀ</SoftButton>
                  <SoftBox display="flex" gap={1}>
                    {containView && (
                      <SoftButton variant="gradient" color="success" onClick={handlePrint}>
                        IN BẢNG
                      </SoftButton>
                    )}
                    {!containView && (
                      <SoftButton variant="gradient" color="dark" onClick={submit} disabled={loadingSubmit}>
                        {loadingSubmit ? "ĐANG LƯU..." : id ? "CẬP NHẬT" : "TẠO"}
                      </SoftButton>
                    )}
                  </SoftBox>
                </SoftBox>
              </SoftBox>
            </Card>
          </Grid>
        </Grid>
      </SoftBox>
    </DashboardLayout>
  );
}
